import type { FC } from 'react';
import { Button, Card, FormSelect, EntitySearchSelect, FormDateInput, TradingStrategySelect } from '@components/UI';
import TradeFilterItem from './TradeFilterItem';
import type { TradeFilter, EntityTypeUnion } from '@einhaus/project314-types';

interface FilterBacktestsFiltersProps {
    selectedStrategyId: string;
    selectedEntityType: string;
    selectedEntity: { entityId: number; entityType: EntityTypeUnion['entityType']; symbol?: string; name?: string } | null;
    beforeDate: string;
    tradeFilters: TradeFilter[];
    entityTypeOptions: { value: string; label: string; disabled?: boolean }[];
    isSearchDisabled: boolean;
    isSearchLoading: boolean;
    onChangeStrategyId: (value: string) => void;
    onChangeEntityType: (value: string) => void;
    onChangeEntity: (value: { entityId: number; entityType: EntityTypeUnion['entityType']; symbol?: string; name?: string } | null) => void;
    onChangeBeforeDate: (value: string) => void;
    onOpenFilterModal: () => void;
    onSearch: () => void;
    onRemoveFilter: (id: string) => void;
}

const FilterBacktestsFilters: FC<FilterBacktestsFiltersProps> = ({
    selectedStrategyId,
    selectedEntityType,
    selectedEntity,
    beforeDate,
    tradeFilters,
    entityTypeOptions,
    isSearchDisabled,
    isSearchLoading,
    onChangeStrategyId,
    onChangeEntityType,
    onChangeEntity,
    onChangeBeforeDate,
    onOpenFilterModal,
    onSearch,
    onRemoveFilter
}) => {
    const entityTypeFilter =
        selectedEntityType === ''
            ? null
            : selectedEntityType === 'AssetOption'
              ? 'Asset'
              : (selectedEntityType as EntityTypeUnion['entityType']);

    return (
        <Card>
            <div className="p-4">
                <div className="flex flex-wrap gap-3 mb-4">
                    <div className="min-w-[350px]">
                        <TradingStrategySelect
                            noMargin
                            id="strategySelect"
                            label="Strategy"
                            value={selectedStrategyId ? Number(selectedStrategyId) : null}
                            onChange={(strategyId) => onChangeStrategyId(strategyId ? strategyId.toString() : '')}
                        />
                    </div>
                    <div className="min-w-[250px]">
                        <FormSelect
                            noMargin
                            id="entityTypesSelect"
                            label="Entity Types"
                            options={entityTypeOptions}
                            value={selectedEntityType}
                            onChange={(event) => onChangeEntityType(event.target.value)}
                        />
                    </div>
                    <div className="min-w-[300px]">
                        <EntitySearchSelect
                            disabled={!selectedEntityType}
                            entityTypeFilter={entityTypeFilter}
                            id="entity-search-select"
                            label="Entity (Optional)"
                            placeholder={selectedEntityType ? 'Search for an entity...' : 'Select entity type first'}
                            value={selectedEntity}
                            onChange={onChangeEntity}
                        />
                    </div>
                    <div className="min-w-[200px]">
                        <FormDateInput
                            noMargin
                            id="beforeDateInput"
                            label="Before Date (Optional)"
                            value={beforeDate}
                            onChange={(event) => onChangeBeforeDate(event.target.value)}
                        />
                    </div>
                    <div className="flex items-end">
                        <Button variant="secondary" onClick={onOpenFilterModal}>
                            + Add Filter
                        </Button>
                    </div>
                    <div className="flex items-end">
                        <Button disabled={isSearchDisabled} isLoading={isSearchLoading} variant="primary" onClick={onSearch}>
                            Search
                        </Button>
                    </div>
                </div>

                {tradeFilters.length > 0 && (
                    <div className="mb-4">
                        <h5 className="mb-2">Applied Filters</h5>
                        <div className="flex flex-wrap">
                            {tradeFilters.map((filter) => (
                                <TradeFilterItem filter={filter} key={filter.id} onRemove={onRemoveFilter} />
                            ))}
                        </div>
                    </div>
                )}
                {selectedEntity && (
                    <div className="mb-2">
                        <div className="inline-flex items-center gap-2 rounded-full bg-zinc-800 px-3 py-1 text-xs text-zinc-100">
                            <span className="font-mono">
                                {`${selectedEntity.symbol ?? 'Entity'} (${selectedEntity.entityId}-${selectedEntity.entityType})`}
                            </span>
                            <Button size="sm" variant="outline-dark" onClick={() => onChangeEntity(null)}>
                                Clear
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Card>
    );
};

export default FilterBacktestsFilters;
